import PropTypes from 'prop-types';
import { TextField } from '@mui/material';
import { sanitizeInput } from '../../utils/validation';

/**
 * Reusable Input Component
 * Wraps Material UI TextField with consistent styling
 * Automatically sanitizes input to prevent XSS attacks
 *
 * @param {String} label - Input label
 * @param {String} value - Input value
 * @param {Function} onChange - Change handler
 * @param {String} type - Input type (text, password, email, etc.)
 * @param {Boolean} required - Whether field is required
 * @param {Boolean} autoFocus - Whether to autofocus
 * @param {Boolean} fullWidth - Whether to take full width
 * @param {Object} sx - Additional Material UI sx styles
 * @param {String} placeholder - Placeholder text
 * @param {Boolean} disableSanitize - Disable automatic HTML sanitization (use with caution)
 */
function Input({
    label,
    value,
    onChange,
    type = 'text',
    required = false,
    autoFocus = false,
    fullWidth = true,
    sx = {},
    placeholder = '',
    id,
    name,
    'data-testid': dataTestId,
    slotProps,
    multiline = false,
    rows,
    disableSanitize = false,
    endAdornment,
    ...otherProps
}) {
    // If value is explicitly undefined, use uncontrolled input
    const isControlled = value !== undefined;
    // Sanitize onChange to prevent XSS
    const handleChange = (e) => {
        // Safety check: ensure onChange is a function
        if (!onChange || typeof onChange !== 'function') {
            console.warn('Input component: onChange is not a function', { onChange, label, id, type });
            return;
        }

        // For number type, don't sanitize - allow all input for validation later
        if (type === 'number') {
            // Ensure name is preserved in the event
            const syntheticEvent = {
                ...e,
                target: {
                    ...e.target,
                    name: e.target.name || name, // Use name from event or fallback to prop
                    value: e.target.value, // Keep as string, don't convert to number yet
                },
            };
            onChange(syntheticEvent);
            return;
        }

        if (!disableSanitize && type === 'text') {
            const originalValue = e.target.value;
            const sanitizedValue = sanitizeInput(originalValue);

            // Only modify if sanitization changed the value
            if (sanitizedValue !== originalValue) {
                // Create a more complete synthetic event
                const syntheticEvent = {
                    ...e,
                    target: {
                        ...e.target,
                        value: sanitizedValue,
                    },
                    currentTarget: {
                        ...e.currentTarget,
                        value: sanitizedValue,
                    },
                };
                onChange(syntheticEvent);
            } else {
                // Value unchanged, pass through original event
                onChange(e);
            }
        } else {
            onChange(e);
        }
    };

    // Determine if we have a valid label
    const hasLabel = label && label.trim();

    return (
        <TextField
            {...(hasLabel ? { label } : {})}
            {...(isControlled ? { value: value || '' } : {})}
            onChange={handleChange}
            type={type}
            required={required}
            autoFocus={autoFocus}
            fullWidth={fullWidth}
            name={name}
            variant="outlined"
            size="small"
            InputLabelProps={
                hasLabel
                    ? {
                          shrink: true,
                      }
                    : undefined
            }
            multiline={multiline}
            rows={rows}
            slotProps={{
                ...slotProps,
                input: {
                    endAdornment: endAdornment,
                    notched: hasLabel, // Only show notch when there's a label
                    sx: {
                        ...(multiline ? {} : { padding: '1px 2px' }),
                        fontSize: '14px',
                        lineHeight: '1.5',
                        // Hide number input spinners for consistent styling
                        '& input[type=number]': {
                            MozAppearance: 'textfield',
                        },
                        '& input[type=number]::-webkit-outer-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                        },
                        '& input[type=number]::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                        },
                    },
                },
            }}
            inputProps={{
                ...slotProps?.htmlInput, // Merge htmlInput props (like min, max, step) first
                id,
                name, // Ensure name is set after merging to prevent override
                'data-testid': dataTestId,
            }}
            sx={{
                // Apply placeholder/content padding only when multiline
                ...(multiline
                    ? {
                          '& .MuiOutlinedInput-inputMultiline': { padding: '7.5px 4px' },
                          '& .MuiInputBase-inputMultiline': { padding: '7.5px 4px' },
                      }
                    : {}),
                // Prevent label from wrapping and overlapping border
                '& .MuiInputLabel-root': {
                    whiteSpace: 'nowrap',
                },
                // Remove spacing when no label - make it flush
                ...(!hasLabel
                    ? {
                          marginTop: '0 !important',
                          marginBottom: '0 !important',
                          paddingTop: '0 !important',
                          paddingBottom: '0 !important',
                          '& .MuiOutlinedInput-root': {
                              marginTop: '0 !important',
                              marginBottom: '0 !important',
                              paddingTop: '0 !important',
                              paddingBottom: '0 !important',
                          },
                          '& .MuiInputBase-root': {
                              marginTop: '0 !important',
                              marginBottom: '0 !important',
                              paddingTop: '0 !important',
                              paddingBottom: '0 !important',
                          },
                          '& .MuiInputLabel-root': {
                              display: 'none !important',
                              height: '0 !important',
                              margin: '0 !important',
                              padding: '0 !important',
                              lineHeight: 0,
                          },
                          // Completely hide the notchedOutline legend when no label
                          '& .MuiOutlinedInput-notchedOutline': {
                              top: '0 !important',
                              '& legend': {
                                  display: 'none !important',
                                  visibility: 'hidden !important',
                                  width: '0 !important',
                                  maxWidth: '0 !important',
                                  minWidth: '0 !important',
                                  height: '0 !important',
                                  margin: '0 !important',
                                  padding: '0 !important',
                                  lineHeight: '0 !important',
                                  fontSize: '0 !important',
                              },
                              '& legend span': {
                                  display: 'none !important',
                                  visibility: 'hidden !important',
                                  width: '0 !important',
                                  height: '0 !important',
                                  margin: '0 !important',
                                  padding: '0 !important',
                                  lineHeight: '0 !important',
                                  fontSize: '0 !important',
                              },
                          },
                          // Remove any spacing from the form control wrapper
                          '& .MuiFormControl-root': {
                              marginTop: '0 !important',
                              marginBottom: '0 !important',
                          },
                      }
                    : {
                          // When has label, apply normal legend styles
                          '& .MuiOutlinedInput-notchedOutline': {
                              '& legend': {
                                  maxWidth: 'none !important',
                              },
                              '& legend span': {
                                  paddingLeft: '10px',
                                  paddingRight: '10px',
                              },
                          },
                      }),
                ...sx,
            }}
            placeholder={placeholder}
            {...otherProps}
        />
    );
}

Input.propTypes = {
    label: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    type: PropTypes.string,
    required: PropTypes.bool,
    autoFocus: PropTypes.bool,
    fullWidth: PropTypes.bool,
    sx: PropTypes.object,
    placeholder: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
    'data-testid': PropTypes.string,
    slotProps: PropTypes.object,
    multiline: PropTypes.bool,
    rows: PropTypes.number,
    disableSanitize: PropTypes.bool,
    endAdornment: PropTypes.node,
};

export default Input;
